import { Backdrop, Stack, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { OrderModel, OrderStatusModel } from '@merchant-clients/order';

import { AppHeader, PullToRefresh, useToast, View } from '@frontend/components';
import { useOrdersQuery, useTimeslotOrdersQuery } from '@frontend/data';
import { useAudio, usePullToRefresh } from '@frontend/hooks';
import { tApp } from '@frontend/locales';
import { usePersistentUiSettings } from '@frontend/utils';
import { useAutomaticPrintQueue } from '@frontend/utils/printing/use-automatic-print-queue';

import {
  FilterDisplay,
  OrderActionBar,
  OrderDetailsDrawer,
  OrderHeaderAction,
  OrderList,
  OrderTimeslotList,
} from './components';
import {
  OrderFilterInput,
  orderFilterBasicPeriodMapper,
} from './components/filter';
import { useOrderGrouping } from './hooks';
import { sortTimeslotGroups } from './hooks/sort-timeslot-groups';

export const OrdersView = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const orderIdParam = searchParams.get('orderId');
  const { showToast } = useToast();
  const { shopId } = useParams();
  const [toggleDetails, setToggleDetails] = useState(false);
  const [orders, setOrders] = useState<OrderModel[] | undefined>(undefined);
  const isFilterChanged = useRef<boolean>(false);
  const {
    ordersPage: { groupBy, sortField, sortDirection, filter, audio },
    updateOrdersPage,
  } = usePersistentUiSettings();
  const { groupedOrders } = useOrderGrouping({
    orders: orders ?? [],
  });
  const { playAudio } = useAudio();

  const { queueOrdersForPrinting } = useAutomaticPrintQueue();

  const { useRegisterUpdate, completed } = usePullToRefresh({
    operationKey: 'orders',
  });

  const refetchOrders = () => {
    completed();
  };

  useRegisterUpdate(() => {
    return refetchOrders();
  });

  const handlePeriodMapper = () => {
    const mappedPeriod = orderFilterBasicPeriodMapper(filter.period);
    return {
      from: mappedPeriod.from.toISOString(),
      to: mappedPeriod.to.toISOString(),
    };
  };

  const orderFilter = {
    status: filter.status,
    placeIds: filter.placeIds,
    shopId: shopId!,
    period: handlePeriodMapper(),
  };

  const { data: fetchedOrders, refetch } = useOrdersQuery({
    preOrder: filter.preOrder,
    filter: orderFilter,
    onError: (error) => {
      showToast({
        header: tApp.errorPages.unexpectedError(),
        body: tApp.errorPages.unexpectedErrorDescription(),
        severity: 'error',
      });
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to fetch orders for shop ${shopId}`,
        },
      });
    },
  });

  const { data: timeslotGroups, refetch: refetchTimeslot } =
    useTimeslotOrdersQuery({
      filter: orderFilter,
      enabled: groupBy === 'TIMESLOT',
      onError: (error) => {
        showToast({
          header: tApp.errorPages.unexpectedError(),
          body: tApp.errorPages.unexpectedErrorDescription(),
          severity: 'error',
        });
        captureException(error, {
          level: 'error',
          extra: {
            message: `Failed to fetch timeslot orders for shop ${shopId}`,
          },
        });
      },
    });

  const sortedTimeslotGroups = useMemo(
    () => sortTimeslotGroups(timeslotGroups ?? [], sortField, 'ASC'),
    [timeslotGroups, sortField],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: filter is the trigger, refetch whenever it changes
  useEffect(() => {
    refetch();
    if (groupBy === 'TIMESLOT') {
      refetchTimeslot();
    }
  }, [filter, groupBy, refetch, refetchTimeslot]);

  /**
   * Currently, this is not triggered on refetch.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per fetch. It sets orders itself and compares them with the previous fetch to play the new-order sound, so depending on orders would loop and requeue orders for printing.
  useEffect(() => {
    if (!fetchedOrders) {
      return;
    }

    let freshOrders = fetchedOrders;

    if (searchText !== '') {
      const search = fetchedOrders.filter((order) => {
        return order.verification?.toString().includes(searchText);
      });
      freshOrders = search;
    }

    if (audio.enabled && !isFilterChanged.current && orders) {
      const isNewOrder =
        freshOrders?.some((order) => {
          return !orders?.some((o) => o.id === order.id);
        }) ?? false;

      if (isNewOrder) {
        playAudio();
      }
    }

    if (isFilterChanged.current) {
      isFilterChanged.current = false;
    }

    // This effect plays the new-order sound by comparing against the previous
    // orders state and queues orders for printing, and onHandleSearch also writes
    // orders. Deriving the list would need all three reworked, so it stays an effect.
    setOrders(freshOrders);

    queueOrdersForPrinting(
      freshOrders.filter(
        (order) =>
          order.status === 'CONFIRMED' &&
          typeof order.verification === 'number',
      ),
    );
  }, [fetchedOrders]);

  const onHandleSearch = (input: string) => {
    setSearchText(input);

    if (!fetchedOrders) {
      return;
    }

    let ordersToSearch = fetchedOrders;

    if (input !== '') {
      ordersToSearch = fetchedOrders.filter((order) => {
        return order.verification?.toString().includes(input);
      });
    }

    setOrders(ordersToSearch);
  };

  const onFilterChange = (input: OrderFilterInput) => {
    isFilterChanged.current = true;
    updateOrdersPage({
      filter: {
        placeIds: input.placeIds,
        preOrder: input.preOrder ? Number(input.preOrder) : undefined,
        status: input.status as OrderStatusModel[],
        period: input.period,
      },
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        searchParams.delete('orderId');
        setSearchParams(searchParams, { replace: true });
      }
    };

    if (orderIdParam !== null) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [orderIdParam, searchParams, setSearchParams]);

  return (
    <>
      <Backdrop
        open={orderIdParam !== null}
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
        onClick={() => {
          searchParams.delete('orderId');
          setSearchParams(searchParams, { replace: true });
        }}
      />

      <View
        header={
          <AppHeader
            action={
              <OrderHeaderAction
                onSearch={onHandleSearch}
                onFilter={onFilterChange}
                onGroupByChange={(input) =>
                  updateOrdersPage({ groupBy: input })
                }
                onSortDirectionChange={(input) =>
                  updateOrdersPage({ sortDirection: input })
                }
                selectedGroupBy={groupBy}
                selectedSortDirection={sortDirection}
              />
            }
          />
        }
      >
        <PullToRefresh operationKeys={['orders']}>
          <Stack p={0} height="100%">
            <FilterDisplay filter={filter} />
            {groupBy === 'TIMESLOT' ? (
              <OrderTimeslotList groups={sortedTimeslotGroups} />
            ) : (
              <OrderList orders={groupedOrders} groupBy={groupBy} />
            )}
          </Stack>
        </PullToRefresh>
        <OrderDetailsDrawer
          orders={orders ?? []}
          openState={toggleDetails}
          toggleDrawer={() => {
            setToggleDetails(false);
            searchParams.delete('orderId');
            setSearchParams(searchParams, { replace: true });
          }}
        />
        <OrderActionBar
          orders={orders ?? []}
          toggleDetails={setToggleDetails}
        />
      </View>
    </>
  );
};
